import type { DadoDaSala, ParticipanteDaSala } from '@orbind/core'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router'
import { api, apiOrigin } from '../../lib/api.ts'
import { useSession } from '../../lib/session.tsx'
import { aplicarSaida, guardarAparelho, restricaoDeAudio, restricaoDeVideo } from '../chat/dispositivos.ts'
import {
  aparenciaInicial,
  aplicarFundoNaCamera,
  estiloDeFundoGuardado,
  guardarEstiloDeFundo,
  imagemDeFundoGuardada,
  type EstiloDeFundo,
  type FundoAplicado,
} from '../chat/fundo.ts'
import { MalhaDaSala, type ParticipanteAoVivo } from '../chat/sala-mesh.ts'
import { canalDePresenca } from '../presence/presence-client.ts'
import { JanelinhaDaChamada } from './JanelinhaDaChamada.tsx'

/**
 * A chamada de uma sala do Escritório (SPEC-20261010-1557).
 *
 * Clicar numa sala põe a pessoa na chamada sem tirar ela do tabuleiro: a voz
 * corre aqui, no casco do Orbind, e segue viva em qualquer tela. A reunião
 * (quadros, tela compartilhada, chat) é só um jeito de olhar a mesma chamada:
 * abre dentro do Orbind quando alguém liga a câmera ou apresenta a tela, e fora
 * dela a chamada vira uma janelinha que se arrasta.
 *
 * Uma chamada por vez: entrar noutra sala sai da anterior.
 */

export type Estado = { mic: boolean; mao: boolean }
export type Mensagem = { id: string; de: string; nome: string; texto: string; hora: number; minha: boolean }
export type Reacao = { id: number; emoji: string; nome: string; x: number }
export type Quadro = {
  id: string
  nome: string
  foto: string | null
  camera: MediaStream | null
  video: boolean
  audio: MediaStreamTrack | null
  mic: boolean
  mao: boolean
  eu: boolean
}
export type SalaDaChamada = {
  salaId: string
  roomId: string
  nome: string
  template: string
  /** Gente de exemplo da sala (teste do tabuleiro): aparece em quadros, sem mídia. */
  exemplo: { nome: string; foto: string | null }[]
}
export type Apresentacao = { id: string; nome: string; tela: MediaStream | null }

type Chamada = {
  sala: SalaDaChamada | null
  entrando: string | null
  entrar: (s: { id: string; name: string; template: string; exemplo?: SalaDaChamada['exemplo'] }) => Promise<void>
  sair: () => void
  micLigado: boolean
  camLigada: boolean
  apresentando: boolean
  faixaDoMic: MediaStreamTrack | null
  estiloFundo: EstiloDeFundo
  imagemFundo: string | null
  alternarMic: () => void
  alternarCamera: () => void
  apresentar: () => Promise<void>
  trocarMicrofone: (id: string) => void
  trocarCamera: (id: string) => void
  trocarSaida: () => void
  trocarFundo: (estilo: EstiloDeFundo, imagem?: string | null) => Promise<void>
  quadros: Quadro[]
  quemApresenta: Apresentacao | null
  /** Alguém (eu também) está com a câmera ligada ou apresentando: a reunião tem o que mostrar. */
  temVideo: boolean
  mao: boolean
  alternarMao: () => void
  mensagens: Mensagem[]
  naoLidas: number
  chatAberto: boolean
  abrirChat: (aberto: boolean) => void
  enviarMensagem: (texto: string) => void
  reacoes: Reacao[]
  reagir: (emoji: string) => void
  balao: Mensagem | null
  aviso: string | null
}

const Contexto = createContext<Chamada | null>(null)
/** A chamada do Escritório em andamento; `null` fora do casco do Orbind. */
export const useChamada = () => useContext(Contexto)

const novoId = () => Math.random().toString(36).slice(2, 10)
const fotoDoMembro = (memberId: string | null) => (memberId ? `${apiOrigin()}/public/member-avatar/${memberId}` : null)

/** A voz de quem está na chamada: um elemento de áudio por pessoa, sempre montado. */
function Voz({ stream }: { stream: MediaStream | null }) {
  const ref = useRef<HTMLAudioElement>(null)
  useEffect(() => {
    const a = ref.current
    if (!a) return
    if (a.srcObject !== stream) a.srcObject = stream
    void aplicarSaida(a)
    if (stream) void a.play().catch(() => undefined)
  }, [stream])
  return <audio ref={ref} autoPlay />
}

export function ChamadaDoEscritorioProvider({ children }: { children: ReactNode }) {
  const sessao = useSession()
  const local = useLocation()
  const minhaFoto = sessao.hasAvatar ? `${apiOrigin()}/public/member-avatar/${sessao.id}?v=${encodeURIComponent(sessao.avatarVersion ?? '')}` : sessao.avatarUrl

  const [sala, setSala] = useState<SalaDaChamada | null>(null)
  const [entrando, setEntrando] = useState<string | null>(null)
  const microfone = useRef<MediaStreamTrack | null>(null)
  const cameraBruta = useRef<MediaStreamTrack | null>(null)
  const fundo = useRef<FundoAplicado | null>(null)
  const tela = useRef<MediaStreamTrack | null>(null)
  const malha = useRef<MalhaDaSala | null>(null)
  const [micLigado, setMicLigado] = useState(true)
  const [camLigada, setCamLigada] = useState(false)
  const [apresentando, setApresentando] = useState(false)
  const [minhaCamera, setMinhaCamera] = useState<MediaStream | null>(null)
  const [minhaTela, setMinhaTela] = useState<MediaStream | null>(null)
  const [faixaDoMic, setFaixaDoMic] = useState<MediaStreamTrack | null>(null)
  const [estiloFundo, setEstiloFundo] = useState<EstiloDeFundo>(estiloDeFundoGuardado())
  const [imagemFundo, setImagemFundo] = useState<string | null>(imagemDeFundoGuardada())
  const [participantes, setParticipantes] = useState<ParticipanteAoVivo[]>([])
  const [eu, setEu] = useState<ParticipanteDaSala | null>(null)
  const [estados, setEstados] = useState<Record<string, Estado>>({})
  const [mao, setMao] = useState(false)
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [chatAberto, setChatAberto] = useState(false)
  const [naoLidas, setNaoLidas] = useState(0)
  const [reacoes, setReacoes] = useState<Reacao[]>([])
  const [aviso, setAviso] = useState<string | null>(null)
  const [balao, setBalao] = useState<Mensagem | null>(null)

  // os ouvintes do canal leem o estado mais novo por aqui
  const ref = useRef({ participantes, eu, chatAberto, micLigado, mao })
  ref.current = { participantes, eu, chatAberto, micLigado, mao }

  const avisar = useCallback((t: string) => {
    setAviso(t)
    window.setTimeout(() => setAviso((a) => (a === t ? null : a)), 3500)
  }, [])

  /* --------------------------------------------------------- mídia --- */

  const videoParaEnviar = () => fundo.current?.faixa ?? cameraBruta.current
  const mostrarMinhaCamera = () => {
    const v = videoParaEnviar()
    setMinhaCamera(v ? new MediaStream([v]) : null)
  }
  const contar = (dado: DadoDaSala) => canalDePresenca.contarParaASala(dado)
  const contarEstado = (mic = ref.current.micLigado, maoLevantada = ref.current.mao) => contar({ tipo: 'estado', mic, mao: maoLevantada })
  const midiaMudou = async () => {
    await malha.current?.atualizarMidia()
    malha.current?.anunciarMidia(videoParaEnviar() !== null, tela.current !== null)
  }

  const pegarMicrofone = async () => {
    try {
      const m = await navigator.mediaDevices.getUserMedia({ audio: restricaoDeAudio() })
      microfone.current?.stop()
      const faixa = m.getAudioTracks()[0] ?? null
      if (faixa) faixa.enabled = ref.current.micLigado
      microfone.current = faixa
      setFaixaDoMic(faixa)
    } catch {
      setMicLigado(false)
      avisar('Sem acesso ao microfone. Libere o microfone no navegador para falar.')
    }
  }

  const ligarCamera = async () => {
    try {
      const m = await navigator.mediaDevices.getUserMedia({ video: restricaoDeVideo() })
      const bruta = m.getVideoTracks()[0] ?? null
      if (!bruta) return
      cameraBruta.current = bruta
      fundo.current = await aplicarFundoNaCamera(bruta, estiloFundo, imagemFundo, aparenciaInicial()).catch(() => null)
      setCamLigada(true)
      mostrarMinhaCamera()
      await midiaMudou()
    } catch {
      avisar('Sem acesso à câmera. Libere a câmera no navegador.')
    }
  }
  const desligarCamera = async () => {
    fundo.current?.parar()
    fundo.current = null
    cameraBruta.current?.stop()
    cameraBruta.current = null
    setCamLigada(false)
    setMinhaCamera(null)
    await midiaMudou()
  }
  const pararTela = async () => {
    tela.current?.stop()
    tela.current = null
    setApresentando(false)
    setMinhaTela(null)
    await midiaMudou()
  }

  /** Solta tudo o que a chamada pegou: microfone, câmera, tela e a conexão. */
  const soltarMidia = () => {
    microfone.current?.stop()
    microfone.current = null
    fundo.current?.parar()
    fundo.current = null
    cameraBruta.current?.stop()
    cameraBruta.current = null
    tela.current?.stop()
    tela.current = null
    setFaixaDoMic(null)
    setCamLigada(false)
    setMinhaCamera(null)
    setApresentando(false)
    setMinhaTela(null)
  }

  const sair = useCallback(() => {
    setSala(null)
    setChatAberto(false)
    soltarMidia()
    // a mudança de sala desmonta a conexão (efeito abaixo)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const entrar = useCallback<Chamada['entrar']>(
    async (s) => {
      if (sala?.salaId === s.id) return
      setEntrando(s.id)
      try {
        const { roomId } = (await (await api.office.rooms[':id'].enter.$post({ param: { id: s.id } })).json()) as { roomId: string }
        if (sala) soltarMidia()
        setMensagens([])
        setNaoLidas(0)
        setEstados({})
        setParticipantes([])
        setMao(false)
        setEu(null)
        setMicLigado(true)
        ref.current.micLigado = true
        await pegarMicrofone()
        setSala({ salaId: s.id, roomId, nome: s.name, template: s.template, exemplo: s.exemplo ?? [] })
      } catch (e) {
        avisar(e instanceof Error ? e.message : 'Não foi possível entrar na sala.')
      } finally {
        setEntrando(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sala, avisar],
  )

  const alternarCamera = () => void (camLigada ? desligarCamera() : ligarCamera())
  const alternarMic = () => {
    const novo = !micLigado
    if (microfone.current) microfone.current.enabled = novo
    setMicLigado(novo)
    contarEstado(novo)
  }
  const apresentar = async () => {
    if (apresentando) {
      await pararTela()
      return
    }
    try {
      const m = await navigator.mediaDevices.getDisplayMedia({ video: true })
      const faixa = m.getVideoTracks()[0] ?? null
      if (!faixa) return
      tela.current = faixa
      faixa.addEventListener('ended', () => void pararTela())
      setApresentando(true)
      setMinhaTela(new MediaStream([faixa]))
      await midiaMudou()
    } catch {
      // cancelou a escolha da tela: nada muda
    }
  }
  const trocarMicrofone = (id: string) => {
    guardarAparelho('microfone', id)
    void pegarMicrofone().then(midiaMudou)
  }
  const trocarCamera = (id: string) => {
    guardarAparelho('camera', id)
    if (camLigada) void desligarCamera().then(ligarCamera)
  }
  const trocarSaida = () => {
    document.querySelectorAll<HTMLMediaElement>('.chm-vozes audio, .rs video, .chm-janelinha video').forEach((v) => void aplicarSaida(v))
  }
  const trocarFundo = async (estilo: EstiloDeFundo, imagem?: string | null) => {
    const img = imagem === undefined ? imagemFundo : imagem
    guardarEstiloDeFundo(estilo)
    setEstiloFundo(estilo)
    setImagemFundo(img)
    const bruta = cameraBruta.current
    if (!bruta) return
    if (fundo.current?.ajustar(estilo, img, aparenciaInicial())) return
    fundo.current?.parar()
    fundo.current = await aplicarFundoNaCamera(bruta, estilo, img, aparenciaInicial()).catch(() => null)
    mostrarMinhaCamera()
    await midiaMudou()
  }

  // fechar a aba solta microfone e câmera
  useEffect(() => () => soltarMidia(), [])

  /* ------------------------------------------------------ na sala --- */

  const roomId = sala?.roomId ?? null
  useEffect(() => {
    if (!roomId) return
    let vivo = true
    const minha = new MalhaDaSala({
      enviar: (para, sinal) => canalDePresenca.enviarSinalDaSala(para, sinal),
      aoMudar: (lista) => setParticipantes([...lista]),
      fontes: { audio: () => microfone.current, video: videoParaEnviar, tela: () => tela.current },
      conectarTodos: true,
    })
    malha.current = minha
    const ouvir = canalDePresenca.inscrever((evento) => {
      if (evento.tipo !== 'sala' || evento.roomId !== roomId) return
      const e = evento.evento
      if (e.nome === 'encerrada') {
        avisar('Esta sala foi apagada. A chamada terminou.')
        sair()
        return
      }
      if (e.nome === 'dado') {
        receber(e.de, e.dado)
        return
      }
      if (e.nome === 'entrei') setEu(e.eu)
      if (e.nome === 'saiu') setEstados(({ [e.participanteId]: _, ...resto }) => resto)
      if (e.nome === 'entrou') avisar(`${e.participante.nome} entrou`)
      void minha.tratarEvento(e).then(() => {
        if (e.nome === 'entrei' || e.nome === 'entrou') {
          minha.anunciarMidia(videoParaEnviar() !== null, tela.current !== null)
          contarEstado()
        }
      })
    })
    void (async () => {
      for (let i = 0; i < 20 && vivo; i++) {
        if (canalDePresenca.entrarNaSala(roomId)) return
        await new Promise((r) => window.setTimeout(r, 300))
      }
      if (vivo) avisar('Não foi possível entrar agora. Confira a conexão e tente de novo.')
    })()
    return () => {
      vivo = false
      ouvir()
      canalDePresenca.sairDaSala()
      minha.encerrar()
      malha.current = null
      setParticipantes([])
      setEu(null)
    }
    // a conexão é da sala: muda só quando a sala muda
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId])

  const nomeDe = (id: string) => {
    if (id === ref.current.eu?.id) return 'Você'
    return ref.current.participantes.find((p) => p.id === id)?.nome ?? 'Alguém'
  }
  const receber = (de: string, dado: DadoDaSala) => {
    const minha = de === ref.current.eu?.id
    if (dado.tipo === 'estado') {
      setEstados((e) => ({ ...e, [de]: { mic: dado.mic, mao: dado.mao } }))
      if (dado.mao && !minha) avisar(`${nomeDe(de)} levantou a mão`)
      return
    }
    if (dado.tipo === 'chat') {
      const m: Mensagem = { id: dado.id, de, nome: nomeDe(de), texto: dado.texto, hora: Date.now(), minha }
      setMensagens((l) => (l.some((x) => x.id === m.id) ? l : [...l, m]))
      if (!minha && !ref.current.chatAberto) {
        setNaoLidas((n) => n + 1)
        setBalao(m)
        window.setTimeout(() => setBalao((b) => (b?.id === m.id ? null : b)), 4500)
      }
      return
    }
    const r: Reacao = { id: Math.random(), emoji: dado.emoji, nome: nomeDe(de), x: 8 + Math.random() * 26 }
    setReacoes((l) => [...l.slice(-14), r])
    window.setTimeout(() => setReacoes((l) => l.filter((x) => x.id !== r.id)), 3200)
  }

  const alternarMao = () => {
    const nova = !mao
    setMao(nova)
    contarEstado(micLigado, nova)
  }
  const enviarMensagem = (texto: string) => {
    const t = texto.trim()
    if (t) contar({ tipo: 'chat', id: novoId(), texto: t.slice(0, 2000) })
  }
  const abrirChat = (aberto: boolean) => {
    setChatAberto(aberto)
    if (aberto) setNaoLidas(0)
  }

  /* ------------------------------------------------------- quadros --- */

  const quadros: Quadro[] = useMemo(() => {
    if (!sala) return []
    const meu: Quadro = {
      id: eu?.id ?? 'eu',
      nome: sessao.name,
      foto: minhaFoto,
      camera: minhaCamera,
      video: camLigada,
      audio: faixaDoMic,
      mic: micLigado,
      mao,
      eu: true,
    }
    const outros = participantes.map<Quadro>((p) => ({
      id: p.id,
      nome: p.nome,
      foto: fotoDoMembro(p.memberId),
      camera: p.camera,
      video: p.midia.video && (p.camera?.getVideoTracks().length ?? 0) > 0,
      audio: p.camera?.getAudioTracks()[0] ?? null,
      mic: estados[p.id]?.mic ?? true,
      mao: estados[p.id]?.mao ?? false,
      eu: false,
    }))
    const deExemplo = sala.exemplo.map<Quadro>((p, i) => ({
      id: `exemplo-${i}`,
      nome: p.nome,
      foto: p.foto,
      camera: null,
      video: false,
      audio: null,
      mic: i % 3 !== 1,
      mao: i === 2,
      eu: false,
    }))
    // mão levantada vem primeiro, como no Meet
    return [meu, ...[...outros, ...deExemplo].sort((a, b) => Number(b.mao) - Number(a.mao))]
  }, [sala, eu, sessao.name, minhaFoto, minhaCamera, camLigada, faixaDoMic, micLigado, mao, participantes, estados])

  const quemApresenta: Apresentacao | null = apresentando
    ? { id: eu?.id ?? 'eu', nome: 'Você', tela: minhaTela }
    : (() => {
        const p = participantes.find((x) => x.midia.tela && x.tela)
        return p ? { id: p.id, nome: p.nome, tela: p.tela } : null
      })()
  const temVideo = sala !== null && (quemApresenta !== null || quadros.some((q) => q.video))

  const valor: Chamada = {
    sala,
    entrando,
    entrar,
    sair,
    micLigado,
    camLigada,
    apresentando,
    faixaDoMic,
    estiloFundo,
    imagemFundo,
    alternarMic,
    alternarCamera,
    apresentar,
    trocarMicrofone,
    trocarCamera,
    trocarSaida,
    trocarFundo,
    quadros,
    quemApresenta,
    temVideo,
    mao,
    alternarMao,
    mensagens,
    naoLidas,
    chatAberto,
    abrirChat,
    enviarMensagem,
    reacoes,
    reagir: (emoji) => void contar({ tipo: 'reacao', emoji }),
    balao,
    aviso,
  }

  // no tabuleiro a chamada mora na barra de baixo e na reunião, na tela inteira;
  // nas outras telas do Orbind ela vira a janelinha
  const noEscritorio = local.pathname === '/office' || local.pathname.startsWith('/office/')
  return (
    <Contexto.Provider value={valor}>
      {children}
      <div className="chm-vozes" aria-hidden>
        {participantes.map((p) => (
          <Voz key={p.id} stream={p.camera} />
        ))}
      </div>
      {sala && !noEscritorio ? <JanelinhaDaChamada /> : null}
      {aviso && !local.pathname.startsWith('/office/reuniao') ? (
        <p className="chm-aviso" role="status">
          {aviso}
        </p>
      ) : null}
    </Contexto.Provider>
  )
}
